import { apiBuilder, prefixApi } from "@zodios/core";
import { z } from "zod";

const reportStatusSchema = z.object({
  status: z.union([z.string(), z.number()]),
});

const reportStartSchema = z.object({
  query_type: z.string(),
  subscription_id: z.number().optional(),
  parameters: z.record(z.union([z.string(), z.number(), z.array(z.union([z.string(), z.number()]))])).optional(),
});

const reportStartResponseSchema = z.object({
  detail: z.string(),
  report: z.any(),
});

const amcInstanceSchema = z.object({
  id: z.number(),
  instance_id: z.string(),
  raw: z.object({
    instanceId: z.string(),
    instanceName: z.string(),
    instanceType: z.string(),
  }),
});

const amcInstanceListSchema = z.array(amcInstanceSchema);

const amcAdvertiserSchema = z.object({
  id: z.string(),
  name: z.string().nullish(),
  type: z.string(),
  marketplaceId: z.string().nullish(),
  currentProfile: z.boolean().nullish(),
});

const amcAdvertisersResponseSchema = z.object({
  advertisers: z.array(amcAdvertiserSchema).nullish(),
  nextToken: z.string().nullish(),
  totalCount: z.number().nullish(),
  amc_instance_missing: z.boolean().nullish(),
});

export type AmcAdvertiser = z.infer<typeof amcAdvertiserSchema>;

export const AMC_ADVERTISER_TYPE = {
  sponsoredAds: "SPONSORED_ADS",
  display: "DISPLAY",
} as const;

const amcAdvertiserUpdateSchema = z.object({
  type: z.nativeEnum(AMC_ADVERTISER_TYPE),
  id: z.string(),
  // `null` for DSP advertisers; left out for a sponsored ads entity in a marketplace the app cannot map.
  marketplaceId: z.string().nullish(),
});

export type AmcAdvertiserUpdate = z.infer<typeof amcAdvertiserUpdateSchema>;

const amcInstanceCreationRequestBodySchema = z.object({
  instance_creation_request: z.object({}),
  advertiser_creation_request: z.object({
    advertisersToAdd: z.array(amcAdvertiserUpdateSchema),
    advertisersToRemove: z.array(amcAdvertiserUpdateSchema).optional(),
  }),
  amc_client: z.number(),
});

export type AmcInstanceCreationRequestBody = z.infer<typeof amcInstanceCreationRequestBodySchema>;

/** An org's request to create its AMC instance, then to change the instance's advertisers. */
const amcInstanceCreationRequestSchema = z.object({
  id: z.number(),
  instance_creation_status: z.string(),
  advertiser_creation_status: z.string(),
  // Amazon's last advertiser-update response, stored either parsed or as the raw error body's JSON text.
  // Only its error `details` are kept.
  advertiser_creation_raw: z
    .preprocess(
      (raw) => {
        if (typeof raw !== "string") {
          return raw;
        }
        try {
          return JSON.parse(raw);
        } catch {
          return undefined;
        }
      },
      z.object({ details: z.string().optional() }),
    )
    .catch({}),
});

export type AmcInstanceCreationRequest = z.infer<typeof amcInstanceCreationRequestSchema>;

export const amcApiDefinitions = prefixApi(
  "/amc",
  apiBuilder({
    alias: "getReportStatus",
    method: "get",
    path: "/reports/status/",
    parameters: [{ name: "subscription_id", type: "Query", schema: z.number() }],
    status: 200,
    response: reportStatusSchema,
  })
    .addEndpoint({
      alias: "startReport",
      method: "post",
      path: "/reports/start/",
      parameters: [
        {
          name: "body",
          type: "Body",
          schema: reportStartSchema,
        },
      ],
      status: 200,
      response: reportStartResponseSchema,
    })
    .addEndpoint({
      alias: "getAMCInstances",
      method: "get",
      path: "/instances/",
      status: 200,
      response: amcInstanceListSchema,
    })
    .addEndpoint({
      alias: "getAMCInstanceAdvertisers",
      method: "get",
      path: "/onboarding/accounts",
      parameters: [
        { name: "instance_id", type: "Query", schema: z.string() },
        { name: "marketplace", type: "Query", schema: z.string() },
      ],
      status: 200,
      response: amcAdvertisersResponseSchema,
    })
    .addEndpoint({
      alias: "getAMCInstanceCreationRequests",
      method: "get",
      path: "/onboarding/instances/",
      status: 200,
      // Newest first.
      response: z.object({ results: z.array(amcInstanceCreationRequestSchema) }),
    })
    .addEndpoint({
      alias: "createAMCInstanceCreationRequest",
      method: "post",
      path: "/onboarding/instances/",
      parameters: [{ name: "body", type: "Body", schema: amcInstanceCreationRequestBodySchema }],
      status: 201,
      response: z.unknown(),
    })
    .addEndpoint({
      alias: "updateAMCInstanceCreationRequest",
      method: "patch",
      path: "/onboarding/instances/:requestId/",
      parameters: [{ name: "body", type: "Body", schema: amcInstanceCreationRequestBodySchema }],
      status: 200,
      response: z.unknown(),
    })
    .build(),
);
